import { useEffect, useState, type CSSProperties } from 'react';
import { Webhook } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import LoadingState from '@/components/ui/LoadingState';
import EmptyState from '@/components/ui/EmptyState';
import FieldsetCard from '@/components/ui/FieldsetCard';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import { listMyWebhooks } from './webhooks.service';
import CreateWebhookDrawer from './CreateWebhookDrawer';
import PostCreationModal from './PostCreationModal';
import DeleteWebhookConfirm from './DeleteWebhookConfirm';
import type { WebhookEndpoint, CreatedWebhook } from './types';

function formatDate(iso: string | null): string {
  if (!iso) return 'Nunca';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'Nunca';
  }
}

function formatStatus(item: WebhookEndpoint): { label: string; color: string; bg: string } {
  if (!item.active) return { label: 'Inativo', color: '#6b7280', bg: '#f3f4f6' };
  if (item.failure_count >= 3) return { label: `${item.failure_count} falhas`, color: '#b45309', bg: '#fef3c7' };
  if (item.last_success_at) return { label: 'Ativo', color: '#15803d', bg: '#dcfce7' };
  return { label: 'Aguardando', color: '#1d4ed8', bg: '#dbeafe' };
}

const PAGE_STYLE: CSSProperties = {
  flex: 1,
  background: 'var(--panel)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  padding: '24px 32px',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const PAGE_HEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: '24px',
  gap: '16px',
  flexWrap: 'wrap',
};

const TABLE_STYLE: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
  tableLayout: 'fixed',
};

const TH_STYLE: CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const TD_STYLE: CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
  color: '#374151',
};

export default function WebhooksPage() {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<CreatedWebhook | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);

  const canCreate = hasPermission('WEBHOOK', 'CREATE');
  const canDelete = hasPermission('WEBHOOK', 'DELETE');

  const reload = async () => {
    try {
      const data = await listMyWebhooks();
      setItems(data);
    } catch {
      // Errors surfaced by the service; list stays empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreated = (result: CreatedWebhook) => {
    setCreating(false);
    setJustCreated(result);
    reload();
  };

  const handleDeleted = () => {
    setDeleting(null);
    reload();
  };

  if (loading) {
    return <LoadingState message="Carregando webhooks..." />;
  }

  return (
    <div style={PAGE_STYLE}>

      <div style={PAGE_HEADER_STYLE}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 700,
            color: '#154680',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Webhook size={20} color="#154680" />
            Webhooks
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
            Receba notificações HTTP em tempo real quando dados mudam (pessoas, obras, atividades, OS).
          </p>
        </div>

        {canCreate && (
          <button
            className="btn-add plus-animated"
            onClick={() => setCreating(true)}
            style={{ flexShrink: 0 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <defs>
                <linearGradient id="webhookPlusG" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="21" y2="0">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#154680" />
                </linearGradient>
              </defs>
              <g className="plus-h" stroke="url(#webhookPlusG)"><path d="M5 12h14" /></g>
              <g className="plus-v" stroke="url(#webhookPlusG)"><path d="M12 5v14" /></g>
            </svg>
            <span className="btn-text">Novo webhook</span>
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={Webhook}
          title="Nenhum webhook"
          description="Crie um webhook para receber notificações HTTP em tempo real quando dados do Joshua mudam."
          action={
            canCreate ? (
              <button
                className="btn-add plus-animated"
                onClick={() => setCreating(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <defs>
                    <linearGradient id="webhookEmptyPlusG" gradientUnits="userSpaceOnUse" x1="3" y1="0" x2="21" y2="0">
                      <stop offset="0%" stopColor="#60a5fa" />
                      <stop offset="100%" stopColor="#154680" />
                    </linearGradient>
                  </defs>
                  <g className="plus-h" stroke="url(#webhookEmptyPlusG)"><path d="M5 12h14" /></g>
                  <g className="plus-v" stroke="url(#webhookEmptyPlusG)"><path d="M12 5v14" /></g>
                </svg>
                <span className="btn-text">Criar primeiro webhook</span>
              </button>
            ) : undefined
          }
        />
      ) : (
        <FieldsetCard
          title="Webhooks ativos"
          count={items.length}
          shadow="md"
          style={{ overflow: 'hidden' }}
          contentStyle={{ padding: 0 }}
        >
          <table style={TABLE_STYLE}>
            <thead>
              <tr>
                <th style={{ ...TH_STYLE, width: '40%' }}>URL</th>
                <th style={{ ...TH_STYLE, width: '15%' }}>Eventos</th>
                <th style={{ ...TH_STYLE, width: '15%' }}>Última entrega</th>
                <th style={{ ...TH_STYLE, width: '15%' }}>Status</th>
                {canDelete && (
                  <th style={{ ...TH_STYLE, width: '15%', textAlign: 'right' }}>Ações</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const status = formatStatus(item);
                return (
                  <tr
                    key={item.id}
                    style={{ background: index % 2 === 0 ? '#fff' : '#f9fafb' }}
                  >
                    <td style={TD_STYLE}>
                      <code
                        title={item.url}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color: '#154680',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.url}
                      </code>
                    </td>
                    <td style={TD_STYLE}>
                      <span
                        title={item.events.join('\n')}
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          background: '#eff6ff',
                          color: '#154680',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'default',
                        }}
                      >
                        {item.events.length} evento{item.events.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td style={{ ...TD_STYLE, color: item.last_success_at ? '#374151' : '#9ca3af' }}>
                      {formatDate(item.last_success_at)}
                    </td>
                    <td style={TD_STYLE}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        background: status.bg,
                        color: status.color,
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        {status.label}
                      </span>
                    </td>
                    {canDelete && (
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>
                        <button
                          className="btn-add gf-trash-animated"
                          onClick={() => setDeleting(item)}
                          title="Excluir webhook"
                          style={{ padding: '4px 8px' }}
                          aria-label={`Excluir webhook ${item.url}`}
                        >
                          <TrashAnimatedIcon size={14} />
                          <span className="btn-text">Excluir</span>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FieldsetCard>
      )}

      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#0c4a6e',
        lineHeight: '1.5',
      }}>
        Cada entrega chega com header <code style={{ fontFamily: 'monospace' }}>X-Joshua-Signature: t=&lt;ts&gt;,v1=&lt;hex&gt;</code> —
        valide com HMAC-SHA256 sobre <code style={{ fontFamily: 'monospace' }}>{'{ts}.{body}'}</code>.
        Falhas são reentregues com backoff exponencial em até 10 tentativas (≈5 dias).
      </div>

      <CreateWebhookDrawer
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={handleCreated}
      />

      {justCreated && (
        <PostCreationModal
          webhook={justCreated}
          onClose={() => setJustCreated(null)}
        />
      )}

      <DeleteWebhookConfirm
        item={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={handleDeleted}
      />

    </div>
  );
}
